import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const glassCard = {
  background: 'rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
};

const bgStyle = {
  background: `
    radial-gradient(ellipse at 20% 50%, var(--gradient-warm-1) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, var(--gradient-warm-2) 0%, transparent 40%),
    radial-gradient(ellipse at 50% 80%, var(--gradient-warm-3) 0%, transparent 45%),
    radial-gradient(ellipse at 70% 60%, var(--gradient-warm-4) 0%, transparent 50%),
    var(--gradient-base)
  `,
  backgroundSize: '200% 200%',
  animation: 'splashGradientShift 12s ease infinite',
};

const ForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setError('');
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    if (error) {
      setError('Something went wrong. Please try again.');
    } else {
      setSent(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="min-h-screen flex flex-col px-6 pb-4" style={{ ...bgStyle, paddingTop: 'calc(var(--sat) + 1rem)' }}>
      {/* Back button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/login')}
          className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-95"
          style={{
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <ChevronLeft size={20} className="text-white" />
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full splash-entrance">
        {sent ? (
          /* Success state */
          <div className="rounded-2xl p-6 text-center" style={glassCard}>
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
            >
              <Mail size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Check your email</h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.65)' }}>
              If an account exists for <strong className="text-white">{email.trim()}</strong>, we've sent a password reset link. Check your spam folder if you don't see it.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          /* Form state */
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Reset password</h1>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Please enter your email and we'll send you a password reset link.
              </p>
            </div>

            <div className="rounded-2xl p-6 space-y-4 mb-4" style={glassCard}>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoComplete="email"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl text-base focus:ring-2 focus:outline-none"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                  }}
                  placeholder="you@example.com"
                />
              </div>

              {error && (
                <p className="text-sm text-center" style={{ color: '#F87171' }}>
                  {error}
                </p>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !email.trim()}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base mb-4 transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:hover:brightness-100 flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>

            <p className="text-center text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <button
                onClick={() => navigate('/login')}
                className="font-semibold underline transition-all"
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
